// 지역(사냥터) 정의(소유: 오케스트레이터). 마을 지역 선택 안내·결과 보상·지역별 몹 배율의 단일 출처. docs/HUNT-R2.md.
// 플레이어 문구만 담는다. 개발 사정(모델 재사용 등)은 qaNote에만 적고 화면에 넣지 않는다.
import { MOB_FORMATION, MOBS, REWARD } from './config';
import type { RegionId } from './levels';

export interface RegionReward {
  readonly base: number;
  readonly perKill: number;
  readonly cardMin: number;
  readonly cardMax: number;
}

/** 일반 몹 공세(c018). RegionInfo.mobTempo 참고 */
export interface MobTempo { readonly attackTokens: number; readonly commitAfter: number; readonly cooldownMult: number; readonly windupMult: number }
/** 지역1·2 공세(원래 config 값 그대로) */
export const BASE_TEMPO: MobTempo = { attackTokens: MOB_FORMATION.attackTokens, commitAfter: MOBS.commitAfter, cooldownMult: 1, windupMult: 1 };

export interface RegionInfo {
  readonly id: RegionId;
  /** MAPS[id].name과 같다 */
  readonly name: string;
  /** 난이도 표시 */
  readonly tier: '입문' | '도전';
  /** 한 줄 소개 */
  readonly blurb: string;
  /** 주요 적(플레이어 문구) */
  readonly enemies: readonly string[];
  /** 위험 요소(플레이어 문구). 없으면 빈 배열 */
  readonly hazards: readonly string[];
  readonly boss: string;
  /** 권장(플레이어 문구) */
  readonly recommend: string;
  readonly reward: RegionReward;
  /** 일반 몹(주술사 포함) 체력·피해 배율. 보스는 자기 수치를 쓴다 */
  readonly mobHpMult: number;
  readonly mobDamageMult: number;
  /**
   * 일반 몹 공세(c018 생존 시뮬, tools/sim/combat.ts survivalTable): 동시에 선딜에 들어가는 근접 몹 수(MOB_FORMATION.attackTokens 대신),
   * 가벼운 기본 공격에 끊기지 않는 선딜 비율(MOBS.commitAfter 대신), 공격 뒤 재사용 대기 배율, 경고 데칼 없는 근접 몹 선딜 배율(데칼 공격은 반응 시간 그대로).
   * 지역1·2는 원래 값(BASE_TEMPO)
   */
  readonly mobTempo: MobTempo;
  /** MobKind 순서 이름(대상 표시·결과 문구) */
  readonly mobNames: readonly [string, string, string, string];
  /** 방에 처음 들어갈 때 한 번 보여 줄 짧은 요령(방 인덱스 순, 없으면 빈 문자열) */
  readonly roomTips: readonly string[];
  /** QA·문서 전용(화면 금지) */
  readonly qaNote: string;
}

export const REGIONS: Readonly<Record<RegionId, RegionInfo>> = {
  r1: {
    id: 'r1',
    name: '이끼 유적',
    tier: '입문',
    blurb: '햇살 드는 폐허. 고블린 무리를 몰아 베고 고목 수호자를 쓰러뜨린다.',
    enemies: ['이끼 고블린 무리', '방패 고블린', '가시 멧돼지 돌진'],
    hazards: [],
    boss: '고목 수호자',
    recommend: '처음이라면 여기부터',
    reward: REWARD,
    mobHpMult: 1,
    mobDamageMult: 1,
    mobTempo: BASE_TEMPO,
    mobNames: ['이끼 고블린', '방패 고블린', '가시 멧돼지', '잿버섯 주술사'],
    roomTips: ['', '', '', ''],
    qaNote: 'M1 기존 던전. 수치·배치 변경 없음.',
  },
  r2: {
    id: 'r2',
    name: '잿불 협곡',
    tier: '도전',
    blurb: '용암이 흐르는 협곡. 다리를 골라 건너 후열 주술사를 끊고, 분출을 읽어 피한다.',
    enemies: ['잿버섯 주술사 포격', '흑요석 방패 고블린 벽', '잿불 고블린·화산 멧돼지'],
    hazards: ['용암 분출구: 붉게 변하면 원 밖으로', '용암 웅덩이: 건너면 화상'],
    boss: '잿불 대주술사',
    recommend: '강철 대검 또는 사슬 갑옷 권장',
    reward: { base: 500, perKill: 6, cardMin: 200, cardMax: 400 },
    // D0a 피해 시뮬 확정(content/world.ts REGION_HP_MULT[1] = 2.3, c009 이월): 지역2 몹 HP 기준 배율. 순환 import를 피해 값으로 두고
    // 테스트가 정본 표와 같은지 확인한다(content.test). 받는 피해 배율은 시뮬 범위 밖(생존 시뮬 후속)이라 기존 1.2를 유지한다
    mobHpMult: 2.3,
    mobDamageMult: 1.2,
    mobTempo: BASE_TEMPO,
    mobNames: ['잿불 고블린', '흑요석 방패 고블린', '화산 멧돼지', '잿버섯 주술사'],
    roomTips: [
      '다리 머리의 분출구는 번갈아 터진다. 조용한 다리로 건너 후열 주술사를 베라.',
      '분화구 웅덩이는 지름길이지만 화상을 입는다. 몹은 웅덩이를 건너지 못한다.',
      '대주술사는 거리를 둔다. 가까이 붙으면 화염 고리, 멀면 운석과 균열이 온다.',
    ],
    qaNote: '전용 R2 몹·용(wyrm) 보스 GLB 없음. 고블린·방패·멧돼지 GLB 재사용(지역 색조), 주술사·대주술사는 mob_mushroom_shaman GLB(대주술사는 확대). 고저감은 렌더 낭떠러지 깊이로만 표현, 바닥 판정은 평면.',
  },
  r3: {
    id: 'r3',
    name: '서리 묘지',
    tier: '도전',
    blurb: '얼어붙은 고딕 묘지. 무덤 뒤에 숨어 해골 궁수를 끊고, 서리 리치의 얼음 십자를 비켜 선다.',
    enemies: ['해골 궁수 일제 사격', '얼음 망령 돌진', '서리 정예: 맞으면 몸이 굳는다'],
    hazards: ['얼음 가시: 푸르게 차오르면 원 밖으로', '서리 균열: 밟으면 동상 + 둔화'],
    boss: '묘지기 기사(3-5) · 서리 리치(3-10)',
    recommend: 'Lv 30 이상 · 영웅 등급 무기 +9',
    // 지역 배율 2.2(처치 EXP·보상 같은 계단, 시뮬 REGION_XP_BASE)
    reward: { base: 1100, perKill: 13, cardMin: 440, cardMax: 880 },
    // D0a 피해 시뮬 확정(content/world.ts REGION_HP_MULT[2] = 4.9). 받는 피해 배율·공세는 c018 생존 시뮬 확정(tools/sim/combat.ts survivalTable):
    // 기대 영웅이 일반 스테이지 한 판에 HP 15~30%를 잃게(한 대 = 그 지역 기대 HP의 약 4~6%, 가안 1.7은 봇 평균 손실 7%)
    mobHpMult: 4.9,
    mobDamageMult: 3.4,
    mobTempo: { attackTokens: 4, commitAfter: 0.2, cooldownMult: 0.85, windupMult: 0.8 },
    mobNames: ['서리 구울', '해골 방패병', '얼음 망령', '해골 궁수'],
    roomTips: [
      '해골 궁수는 들판 북쪽 끝에서 쏜다. 무덤을 끼고 붙어라.',
      '가운데 균열은 지름길이지만 몸이 굳는다. 망령 돌진은 균열에서 끊긴다.',
      '종루 조각상을 돌아 들어가면 궁수 사선이 끊긴다.',
      '리치가 손을 들면 십자 얼음줄이 온다. 대각선으로 비켜 서라.',
    ],
    qaNote: 'D3a(c016). 전용 몹·보스 GLB 없음: R1 몹 GLB 넷에 서리 색조, 중간보스는 고목 수호자 모델·지역보스는 잿불 대주술사 모델의 색조·크기 변형. 환경은 R1 유적 키트 + 서리 분위기(mood r3).',
  },
  r4: {
    id: 'r4',
    name: '가라앉은 사원',
    tier: '도전',
    blurb: '독안개가 고인 늪 사원. 둑길을 따라 독 늪을 피하고, 늪의 히드라의 세 갈래 물기를 읽는다.',
    enemies: ['리자드맨 무리', '독충 돌진', '독 정예: 맞으면 중독'],
    hazards: ['독가스 분출구: 초록으로 차오르면 원 밖으로', '독 늪: 들어서면 중독'],
    boss: '독안개 사제(4-5) · 늪의 히드라(4-10)',
    recommend: 'Lv 40 이상 · 영웅 등급 장비 +9',
    reward: { base: 2400, perKill: 29, cardMin: 970, cardMax: 1940 },
    mobHpMult: 10.2,
    // c018 생존 시뮬 확정(가안 2.4는 봇 평균 손실 9%): 지역3보다 공세가 세고 독 상태 피해(한 판 약 10%)가 더해진다
    mobDamageMult: 4.6,
    mobTempo: { attackTokens: 5, commitAfter: 0.1, cooldownMult: 0.75, windupMult: 0.7 },
    mobNames: ['늪 리자드맨', '사원 수호석상', '독충', '독안개 신도'],
    roomTips: [
      '둑 양쪽 늪은 중독된다. 가운데 마른 길로 싸워라.',
      '정원 가운데 구덩이를 돌면 신도의 독구슬이 빗나간다.',
      '기도실 기둥 뒤에서 신도를 먼저 끊어라.',
      '히드라 머리 셋이 차례로 문다. 첫 물기 뒤 옆으로 크게 돌아라.',
    ],
    qaNote: 'D3a(c016). 전용 몹·보스 GLB 없음: R1 몹 GLB 넷에 늪 색조, 중간보스는 잿불 대주술사 모델·지역보스는 고목 수호자 모델의 색조·크기 변형. 환경은 R1 유적 키트 + 늪 분위기(mood r4).',
  },
  r5: {
    id: 'r5',
    name: '황금 모래 폐도',
    tier: '도전',
    blurb: '모래에 묻힌 고대 도시. 열주를 끼고 자칼 사제를 끊고, 유사 구덩이를 비켜 모래 군주의 왕좌에 오른다.',
    enemies: ['미라 병사 무리', '모래 전갈 돌진', '모래 정예: 맞으면 발이 묶인다'],
    hazards: ['모래 간헐천: 금빛으로 차오르면 원 밖으로', '유사: 빠지면 둔화 + 찰과상'],
    boss: '경비대장(5-5) · 모래 군주(5-10)',
    recommend: 'Lv 55 이상 · 영웅 등급 장비 +12',
    // 지역 배율 2.2 계단(처치 EXP·보상, 시뮬 REGION_XP_BASE): 지역4 × 2.2
    reward: { base: 5280, perKill: 64, cardMin: 2130, cardMax: 4270 },
    // 몹 HP = content/world.ts REGION_HP_MULT[4](21.5). 받는 피해는 C1 생존 시뮬 권장(tools/sim/combat.ts survivalTable 지역5·6, PR #283 공식):
    // 다음 지역 1스테이지 손실이 앞 지역 9스테이지보다 커야 한다(survival.test 불변식) — 가안 6.2는 r5-1 0.210 < r4-9 0.247로 어겼다
    mobHpMult: 21.5,
    // 5-1 0.249 > 4-9 0.247, 5-4 0.298 ≤ 0.30, 5-9 0.247 < 6-1(survival.test)
    mobDamageMult: 7.25,
    mobTempo: { attackTokens: 5, commitAfter: 0.1, cooldownMult: 0.72, windupMult: 0.7 },
    mobNames: ['미라 병사', '스카라브 방패병', '모래 전갈', '자칼 사제'],
    roomTips: [
      '자칼 사제는 대로 끝에서 쏜다. 열주를 끼고 붙어라.',
      '가운데 유사 구덩이는 발이 묶인다. 전갈 돌진은 구덩이 둘레에서 끊긴다.',
      '오벨리스크 뒤로 돌면 사제의 모래 구슬이 빗나간다.',
      '모래 군주가 팔을 펼치면 둘레에 모래 소용돌이가 인다. 가운데 틈에 서라.',
    ],
    qaNote: '3개월 C2. 전용 몹·보스 GLB 없음: R1 몹 GLB 넷에 모래·금 색조, 중간보스는 고목 수호자 모델·지역보스는 잿불 대주술사 모델의 색조·크기 변형. 환경은 지역판 키트(r5 사암) + 사막 분위기(mood r5).',
  },
  r6: {
    id: 'r6',
    name: '심연의 성채',
    tier: '도전',
    blurb: '허공에 뜬 흑요석 성채. 좁은 성벽을 뚫고 처형장을 지나 정상의 심연의 군주와 마지막 결전을 벌인다.',
    enemies: ['악마 기사 돌파', '그림자 마수 돌진', '심연 정예: 맞으면 침식'],
    hazards: ['공허 기둥: 보랏빛으로 차오르면 원 밖으로', '심연 균열: 들어서면 침식'],
    boss: '심연 집행자(6-5) · 심연의 군주(6-10)',
    recommend: 'Lv 68 이상 · 세트·전설 장비 +12',
    reward: { base: 11620, perKill: 140, cardMin: 4690, cardMax: 9390 },
    // 몹 HP = REGION_HP_MULT[5](45). 받는 피해 = C1 생존 시뮬 권장(1스테이지 손실 약 0.23, 상한 0.30 = 15.2 이하)
    mobHpMult: 45,
    mobDamageMult: 15.1,
    mobTempo: { attackTokens: 6, commitAfter: 0.08, cooldownMult: 0.7, windupMult: 0.7 },
    mobNames: ['악마 기사', '사슬 망령', '그림자 마수', '심연 주술사'],
    roomTips: [
      '성벽이 좁다. 가운데 균열 양옆 길로 기사 벽을 밀고 나가라.',
      '부서진 탑의 균열은 침식된다. 마수 돌진을 균열 쪽으로 흘려라.',
      '처형대를 돌면 주술사의 공허 구슬이 막힌다.',
      '군주가 하늘을 가르면 여섯 갈래 별빛이 내리꽂힌다. 줄 사이로 비켜 서라.',
    ],
    qaNote: '3개월 C2. 전용 몹·보스 GLB 없음: R1 몹 GLB 넷에 흑요석·보라 색조, 중간보스는 고목 수호자 모델·지역보스는 잿불 대주술사 모델의 색조·크기 변형. 환경은 지역판 키트(r6 흑요석) + 심연 분위기(mood r6).',
  },
};
